/* iter179 — Billing Diagnostics → "By Tech" tab.
   Per-tech rollup of unfinished billing with:
   - tech name, count
   - latest clock-out reason (if any)
   - per-job list: customer, date, label, last action
   - Open Shift link to /admin/schedule#shift=<id> (existing route already
     deep-links by appointment via the JobPreviewSheet path on Schedule.js)
   - Admin/service_manager Override button (asks for reason; calls
     /api/admin/unfinished-billing/{id}/override) — clears the row.
*/
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from './ui/dialog';
import { Users, AlertTriangle, ShieldCheck, ExternalLink, Wrench } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const BillingByTechTab = () => {
  const { user } = useAuth();
  const canOverride = ['super_admin', 'admin', 'service_manager'].includes(user?.role);
  const [days, setDays] = useState(14);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [overrideJob, setOverrideJob] = useState(null);
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const today = new Date();
      const start = new Date(today.getTime() - days * 24 * 3600 * 1000);
      const fmt = (d) => d.toISOString().slice(0, 10);
      const { data: d } = await axios.get(
        `${API}/admin/billing-diagnostics/by-tech?start_date=${fmt(start)}&end_date=${fmt(today)}`,
        { withCredentials: true },
      );
      setData(d);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load');
    }
    setLoading(false);
  };
  useEffect(() => { load(); }, [days]);

  const doOverride = async () => {
    if (!overrideJob) return;
    if (reason.trim().length < 5) {
      toast.error('Reason must be at least 5 characters');
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(
        `${API}/admin/unfinished-billing/${overrideJob.id}/override`,
        { reason: reason.trim() },
        { withCredentials: true },
      );
      toast.success('Override saved — job cleared');
      setOverrideJob(null);
      setReason('');
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Override failed');
    }
    setSubmitting(false);
  };

  return (
    <div data-testid="billing-by-tech-tab" className="space-y-4">
      <div className="bg-[#141414] border border-[#262626] rounded-md p-4 flex flex-wrap items-end gap-3">
        <div>
          <Label className="text-xs text-[#A3A3A3] uppercase tracking-wider">Window (days)</Label>
          <Input
            data-testid="bytech-days"
            type="number" min="1" max="90"
            value={days}
            onChange={(e) => setDays(Number(e.target.value || 14))}
            className="w-24 bg-[#0A0A0A] border-[#262626] text-white mt-1"
          />
        </div>
        <Button data-testid="bytech-refresh" onClick={load} disabled={loading} className="bg-[#D4AF37] text-[#0A0A0A] hover:bg-[#F3E5AB] font-bold">
          {loading ? 'Loading…' : 'Refresh'}
        </Button>
        {data && (
          <p className="text-[#A3A3A3] text-sm ml-auto">
            Total unfinished: <span className="text-red-300 font-bold" data-testid="bytech-total">{data.total}</span>
          </p>
        )}
      </div>

      {data && data.techs.length === 0 && (
        <p data-testid="bytech-empty" className="bg-[#141414] border border-[#262626] rounded-md p-6 text-[#A3A3A3]">
          🎉 No unfinished billing in this window.
        </p>
      )}

      {data && data.techs.map((t) => (
        <div
          key={t.employee_id}
          data-testid={`bytech-row-${t.employee_id}`}
          className="bg-[#141414] border border-[#262626] rounded-md p-4 space-y-3"
        >
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <div>
              <h4 className="font-['Outfit'] text-lg font-semibold text-white flex items-center gap-2">
                <Users className="w-4 h-4 text-[#D4AF37]" /> {t.employee_name}
                <span className="text-red-300 text-sm font-bold">· {t.count} unfinished</span>
              </h4>
              {t.latest_clockout_reason && (
                <p data-testid={`bytech-clockout-reason-${t.employee_id}`} className="text-amber-300 text-xs mt-1 flex items-center gap-1.5">
                  <AlertTriangle className="w-3 h-3" />
                  Last clock-out reason: <strong>{t.latest_clockout_reason.label}</strong>
                  {t.latest_clockout_reason.reason_other && (
                    <span className="text-[#A3A3A3]"> — &quot;{t.latest_clockout_reason.reason_other}&quot;</span>
                  )}
                </p>
              )}
            </div>
          </div>
          <div className="space-y-1.5">
            {t.jobs.map((j) => (
              <div
                key={j.id}
                data-testid={`bytech-job-${j.id}`}
                className="bg-[#0A0A0A] border border-[#262626] rounded p-3 flex flex-wrap items-center gap-3"
              >
                <div className="min-w-0 flex-1">
                  <p className="text-white font-bold truncate">{j.customer_name || '—'}</p>
                  <p className="text-[#A3A3A3] text-xs truncate">
                    {j.date} {j.start_time && <>· {j.start_time}</>} · {j.shift_title || j.service_type || '—'}
                  </p>
                  {j.last_action_step && (
                    <p className="text-[#525252] text-[11px]">
                      Last step: {j.last_action_step} · invoice: {j.last_invoice_status || '—'}
                    </p>
                  )}
                </div>
                <div className="flex items-center gap-1.5">
                  <a
                    href={`/admin/schedule?finish_billing=${j.id}`}
                    data-testid={`bytech-open-${j.id}`}
                    className="inline-flex items-center gap-1 px-2.5 py-1.5 border border-[#262626] text-white hover:bg-[#262626] rounded text-xs"
                  >
                    <ExternalLink className="w-3 h-3" /> Open Shift
                  </a>
                  <a
                    href={`/admin/schedule?finish_billing=${j.id}`}
                    data-testid={`bytech-finish-${j.id}`}
                    className="inline-flex items-center gap-1 px-2.5 py-1.5 bg-[#D4AF37] text-[#0A0A0A] hover:bg-[#F3E5AB] rounded text-xs font-bold"
                  >
                    <Wrench className="w-3 h-3" /> Finish Billing
                  </a>
                  {canOverride && (
                    <button
                      type="button"
                      data-testid={`bytech-override-${j.id}`}
                      onClick={() => { setOverrideJob(j); setReason(''); }}
                      className="inline-flex items-center gap-1 px-2.5 py-1.5 border border-red-500/40 text-red-300 hover:bg-red-500/10 rounded text-xs"
                    >
                      <ShieldCheck className="w-3 h-3" /> Override
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}

      <Dialog open={!!overrideJob} onOpenChange={(o) => { if (!o) { setOverrideJob(null); setReason(''); } }}>
        <DialogContent
          data-testid="billing-override-dialog"
          className="bg-[#141414] border border-red-500/40 text-white"
        >
          <DialogHeader>
            <DialogTitle className="text-red-300 flex items-center gap-2">
              <ShieldCheck className="w-5 h-5" /> Override Unfinished Billing
            </DialogTitle>
            <DialogDescription className="text-[#C9C9C9]">
              Clearing <strong className="text-white">{overrideJob?.customer_name}</strong> ({overrideJob?.date}) from unfinished billing.
              Provide a reason — this creates an audit record.
            </DialogDescription>
          </DialogHeader>
          <div>
            <Label className="text-xs text-[#A3A3A3] uppercase tracking-wider">Reason *</Label>
            <Textarea
              data-testid="override-reason-text"
              rows={3}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="e.g. warranty job — no revenue per HSP rules"
              className="bg-[#0A0A0A] border-[#262626] text-white mt-1"
            />
          </div>
          <DialogFooter className="flex-row gap-2 justify-end">
            <Button variant="outline" onClick={() => { setOverrideJob(null); setReason(''); }} className="border-[#262626] text-white hover:bg-[#262626]">
              Cancel
            </Button>
            <Button data-testid="override-submit" onClick={doOverride} disabled={submitting} className="bg-red-500/20 border border-red-500/40 text-red-300 hover:bg-red-500/30">
              {submitting ? 'Saving…' : 'Override & Clear'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default BillingByTechTab;
